import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { POSTS, findPost } from "../lib/blog";
import { useSEO } from "../hooks/useSEO";
import InstallBadgesRow from "../components/InstallBadgesRow";
import { ArrowLeft, ArrowRight, Clock, Calendar } from "lucide-react";

/**
 * BlogPostPage — public /blog/:slug route.
 *
 * Renders a single post in the user's language with full SEO meta + JSON-LD
 * Article schema injected at runtime (so Google sees per-article structured data).
 */
export default function BlogPostPage() {
  const { slug } = useParams();
  const { language } = useApp();
  const es = language === "es";
  const post = findPost(slug);

  // Inject JSON-LD Article on mount
  useEffect(() => {
    if (!post) return;
    const ld = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": post.title[language] || post.title.es,
      "description": post.description[language] || post.description.es,
      "image": post.hero,
      "datePublished": post.date,
      "dateModified": post.date,
      "author": { "@type": "Organization", "name": "BScanIA", "url": "https://bscan.app/" },
      "publisher": {
        "@type": "Organization",
        "name": "BScanIA",
        "logo": { "@type": "ImageObject", "url": "https://bscan.app/b-icon.png" },
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://bscan.app/blog/${post.slug[language] || post.slug.es}`,
      },
      "keywords": post.keywords,
      "inLanguage": language,
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "blog-article-ld";
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById("blog-article-ld");
      if (el) el.remove();
    };
  }, [post, language]);

  useSEO(
    post && {
      title: `${post.title[language] || post.title.es} · BScanIA`,
      description: post.description[language] || post.description.es,
      canonical: `https://bscan.app/blog/${post.slug[language] || post.slug.es}`,
      lang: language,
      keywords: post.keywords,
      ogImage: post.hero,
    }
  );

  if (!post) return <Navigate to="/blog" replace />;

  const content = post.content[language] || post.content.es;
  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString(es ? "es-ES" : "en-US", {
      year: "numeric", month: "long", day: "numeric",
    });

  // Related posts: same category first, then others
  const related = POSTS.filter((p) => p.id !== post.id).slice(0, 3);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="blog-post-page">
      {/* Header bar */}
      <header className="border-b border-white/10 px-6 lg:px-10 py-4 flex items-center justify-between sticky top-0 bg-[#0A0A0A]/90 backdrop-blur z-40">
        <Link to="/" className="flex items-center gap-2.5" data-testid="post-logo-link">
          <img src="/b-icon.png" alt="BScanIA" className="w-9 h-9 rounded-md" />
          <span className="font-display font-bold text-lg">BScanIA</span>
        </Link>
        <Link
          to="/register"
          className="font-mono text-xs uppercase tracking-[0.15em] text-[#006CFF] hover:text-white transition-colors"
          data-testid="post-cta-link"
        >
          {es ? "Empieza gratis →" : "Start free →"}
        </Link>
      </header>

      <article className="max-w-3xl mx-auto px-6 lg:px-10 py-12 lg:py-20">
        {/* Back link */}
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-[#006CFF] hover:text-white font-mono text-xs uppercase tracking-[0.15em] mb-8 transition-colors"
          data-testid="post-back-to-blog"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          {es ? "Todos los artículos" : "All articles"}
        </Link>

        {/* Meta */}
        <div className="flex items-center gap-3 mb-4 font-mono text-[10px] uppercase tracking-[0.15em] text-white/45">
          <span className="text-[#006CFF]">/ {post.category[language] || post.category.es}</span>
          <span>·</span>
          <Calendar className="w-3 h-3" />
          <span>{fmtDate(post.date)}</span>
          <span>·</span>
          <Clock className="w-3 h-3" />
          <span>{post.readingTime} {es ? "min de lectura" : "min read"}</span>
        </div>

        {/* H1 */}
        <h1 className="font-display font-bold text-3xl lg:text-5xl leading-tight mb-6" data-testid="post-title">
          {post.title[language] || post.title.es}
        </h1>

        {/* Lead */}
        <p className="text-lg lg:text-xl text-white/70 leading-relaxed mb-10" data-testid="post-lead">
          {post.description[language] || post.description.es}
        </p>

        {/* Hero image */}
        <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-12 bg-black border border-white/10">
          <img
            src={post.hero}
            alt={post.title[language] || post.title.es}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Body */}
        <div className="prose-content space-y-5 text-white/85 leading-relaxed text-base lg:text-lg" data-testid="post-body">
          {content.map((block, i) => {
            if (block.t === "h2") {
              return (
                <h2 key={i} className="font-display font-bold text-2xl lg:text-3xl mt-12 mb-3 text-white">
                  {block.x}
                </h2>
              );
            }
            return <p key={i}>{block.x}</p>;
          })}
        </div>

        {/* CTA box */}
        <div className="mt-16 p-7 lg:p-10 rounded-2xl bg-gradient-to-br from-[#006CFF]/15 via-[#7C3AED]/8 to-transparent border border-[#006CFF]/25">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#006CFF] mb-3">
            / {es ? "Da el siguiente paso" : "Take the next step"}
          </div>
          <h3 className="font-display font-bold text-2xl lg:text-3xl mb-3">
            {es ? "Pausa antes de decidir." : "Pause before you decide."}
          </h3>
          <p className="text-white/65 mb-6">
            {es
              ? "Habla con B. 7 días gratis, sin tarjeta. Tu mentor personal con IA para decisiones difíciles."
              : "Talk to B. 7 days free, no card. Your personal AI mentor for hard decisions."}
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#006CFF] hover:bg-[#0052CC] text-white font-display font-semibold transition-colors"
            data-testid="post-bottom-cta"
          >
            {es ? "Probar BScanIA gratis" : "Try BScanIA free"}
            <ArrowRight className="w-4 h-4" />
          </Link>

          {/* Install badges */}
          <div className="mt-10 pt-8 border-t border-white/10">
            <InstallBadgesRow variant="default" align="center" utmSource="blog_post" />
          </div>
        </div>
      </article>

      {/* Related posts */}
      <section className="border-t border-white/10 bg-[#121214] py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <h3 className="font-display font-bold text-2xl lg:text-3xl mb-8">
            {es ? "Sigue leyendo" : "Keep reading"}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {related.map((r) => (
              <Link
                key={r.id}
                to={`/blog/${r.slug[language] || r.slug.es}`}
                className="group block rounded-xl border border-white/10 bg-[#0A0A0A] overflow-hidden hover:border-[#006CFF]/40 transition-colors"
                data-testid={`related-card-${r.id}`}
              >
                <div className="aspect-[16/9] bg-black overflow-hidden">
                  <img
                    src={r.hero}
                    alt={r.title[language] || r.title.es}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-4">
                  <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#006CFF] mb-2">
                    / {r.category[language] || r.category.es}
                  </div>
                  <h4 className="font-display font-semibold text-base leading-snug group-hover:text-[#00C8FF] transition-colors">
                    {r.title[language] || r.title.es}
                  </h4>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
